import { client } from './client'
import { getCsrf, getCookie } from './authentication'
import { CustomError } from '../CustomError'
import { CSRF_TOKEN_NAME } from '@/constants'

/**
 * Create a new document page
 * @param documentId
 * @param values
 * @returns
 */
export const createDocumentPage = async (
  documentVersionId: string,
  values: {
    name: string
    template_values: any
    parent_id: string | null
  },
) => {
  await getCsrf()
  const resp = await client.POST(
    '/api/v1/projects/{document_version_id}/document_pages',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_version_id: documentVersionId,
        },
      },
      body: values,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to create document page'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * list a document pages
 * @param documentVersionId
 * @returns
 */
export const listDocumentPages = async (documentVersionId: string) => {
  const resp = await client.GET(
    '/api/v1/projects/{document_version_id}/document_pages',
    {
      params: {
        path: {
          document_version_id: documentVersionId,
        },
      },
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to list document pages'
    throw new CustomError(resp, message)
  }

  return resp.data
}

export const reorderStepPages = async (
  documentVersionId: string,
  documentIds: string[],
  parentDocumentId: string | null,
) => {
  await getCsrf()
  const resp = await client.POST(
    '/api/v1/projects/{document_version_id}/document_pages/reorder',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_version_id: documentVersionId,
        },
      },
      body: {
        parent_id: parentDocumentId,
        document_page_ids: documentIds,
      },
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to reorder document pages'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Get a document page
 * @param documentPageId
 * @returns
 */
export const getDocumentPage = async (documentPageId: string) => {
  const resp = await client.GET(
    '/api/v1/projects/document_pages/{document_page_id}',
    {
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to get document page'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Update a document page
 * @param documentPageId
 * @param values
 * @returns
 */
export const updateDocumentPage = async (
  documentPageId: string,
  values: {
    name: string
    template_values: any
    parent_id: string | null
  },
) => {
  await getCsrf()
  const resp = await client.PUT(
    '/api/v1/projects/document_pages/{document_page_id}',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
      body: values,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to update document page'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Delete a document page
 * @param documentPageId
 * @returns
 */
export const deleteDocumentPage = async (documentPageId: string) => {
  await getCsrf()
  const resp = await client.DELETE(
    '/api/v1/projects/document_pages/{document_page_id}',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
      parseAs: 'text',
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to delete document page'
    throw new CustomError(resp, message)
  }

  return true
}

export const getDocumentPageInclusiveGroupIds = async (
  documentPageId: string,
) => {
  const resp = await client.GET(
    '/api/v1/projects/document_pages/{document_page_id}/inclusive_assembly_group_ids',
    {
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to get document page inclusive group ids'
    throw new CustomError(resp, message)
  }

  return resp.data
}
